/**
 * Utility for making fetch requests that automatically use API proxies in development
 * to avoid CORS issues.
 */

import { API } from "@/constants/api";

/**
 * Creates a proxied URL for API requests to avoid CORS issues in development
 * @param url The original URL or endpoint path
 * @returns A proxied URL for development or the original URL for production
 */
export function getProxiedUrl(url: string): string {
  // Only proxy in development
  // if (process.env.NODE_ENV !== "development") {
  //   return url;
  // }

  // Check for API endpoints defined in API.ENDPOINTS
  if (Object.values(API.ENDPOINTS).includes(url)) {
    // Special case for marketplace config endpoint - maintain backward compatibility
    if (url === API.ENDPOINTS.MARKETPLACE_CONFIG) {
      return `/api/marketplace-proxy`;
    }

    // For other API endpoints, use the generic proxy - remove leading slash if present
    const cleanPath = url.startsWith('/') ? url.substring(1) : url;
    console.log(`[ProxiedFetch] Converting API endpoint to proxy path: ${url} -> /api/proxy/${cleanPath}`);
    return `/api/proxy/${cleanPath}`;
  }
  
  // If it's a relative URL (not starting with http or /), assume it's a generic API endpoint
  if (!url.startsWith('http') && !url.startsWith('/')) {
    // For other API endpoints, use the generic proxy
    return `/api/proxy/${url}`;
  }
  
  // If it's an absolute URL to our API server
  if (url.startsWith(API.BASE_URL)) {
    // Extract the endpoint part (everything after the base URL)
    const endpoint = url.substring(API.BASE_URL.length);
    
    // Special case for marketplace config - maintain backward compatibility
    if (endpoint === API.ENDPOINTS.MARKETPLACE_CONFIG) {
      return `/api/marketplace-proxy`;
    }
    
    // For other endpoints, use the generic proxy
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    return `/api/proxy/${cleanEndpoint}`;
  }
  
  // For all other URLs (external or local), keep as is
  return url;
}

/**
 * A wrapper around the fetch API that automatically proxies requests in development
 * @param url The URL to fetch
 * @param options Fetch options
 * @returns A Promise resolving to a Response object
 */
export default async function proxiedFetch(
  url: string,
  options?: RequestInit
): Promise<Response> {
  const proxiedUrl = getProxiedUrl(url);
  
  if (proxiedUrl !== url) {
    console.log(`%c[Proxy] Redirecting request:`, 'background: #9C27B0; color: white; padding: 3px; border-radius: 2px;');
    console.log(`%c[Proxy] Original: ${url}`, 'background: #9C27B0; color: white; padding: 3px; border-radius: 2px;');
    console.log(`%c[Proxy] Proxied: ${proxiedUrl}`, 'background: #9C27B0; color: white; padding: 3px; border-radius: 2px;');
  }
  
  return fetch(proxiedUrl, options);
}
